import type { AccountSave } from '../../game/account';
import type { CharacterSave } from '../../game/character';
import type { Progression } from '../../game/Progression';
import {
  type QuestDef,
  type QuestKind,
  type QuestObjective,
  allQuests,
  currentStep,
  isAvailable,
  isReadyToTurnIn,
  nextMainQuest,
} from '../../game/quests';

/**
 * Pure quest-journal rules: which objectives are already satisfied by the
 * world's persistent state, derived tutorial flags, quest status and the
 * grouping used by the journal (J) and the map (M).
 */

// ---------------------------------------------------------------- persistent-state objectives

export type InteractKind =
  'clear' | 'seed' | 'chest' | 'waystone' | 'vista' | 'heart' | 'lore' | 'other';

/** What an `interact` objective target refers to (zone ids follow `<zone>_<kind>_<name>`). */
export function interactKind(target: string): InteractKind {
  if (target.startsWith('clear_')) return 'clear';
  if (target === 'seed') return 'seed';
  if (/(^|_)chest(_|$)/.test(target)) return 'chest';
  if (target.includes('_waystone_')) return 'waystone';
  if (target.includes('_vista_')) return 'vista';
  if (/_heart$/.test(target)) return 'heart';
  if (target.includes('_lore_')) return 'lore';
  return 'other';
}

/** Tutorial flags derived from what the character has already done. */
export const DERIVED_FLAGS: Record<string, (c: CharacterSave, a: AccountSave) => boolean> = {
  tut_tempered: (c) => (c.counters.temperAttempts ?? 0) > 0,
  tut_sigil_read: (c) => Object.values(c.sigilBooks).some((v) => v > 0),
  tut_card_slotted: (_c, a) => a.cards.deck.some((id) => !!id),
  tut_gem_socketed: (c) => c.gems.some((g) => !!g),
  tut_bond: (c) => Object.values(c.bonds).some((v) => v > 0),
  tut_bounty: (c) => (c.counters.bountiesDaily ?? 0) + (c.counters.bountiesWeekly ?? 0) > 0,
};

/**
 * True when an objective is already satisfied by persistent state: a cleared
 * dungeon, an opened chest, an attuned waystone, a claimed vista or heart, a
 * flag. Events for these may have fired before the quest was accepted (or,
 * for chests and waystones, the game fires none), so the quests feature
 * settles them.
 */
export function objectiveSatisfied(c: CharacterSave, a: AccountSave, o: QuestObjective): boolean {
  if (o.type === 'flag') return !!c.flags[o.target] || !!DERIVED_FLAGS[o.target]?.(c, a);
  if (o.type === 'level') return c.level >= Number(o.target);
  if (o.type !== 'interact') return false;
  switch (interactKind(o.target)) {
    case 'clear':
      return !!c.flags[`${o.target.slice('clear_'.length)}_cleared`];
    case 'chest':
      return !!c.flags[`chest_${o.target}`];
    case 'waystone':
      return c.waystones.includes(o.target);
    case 'vista':
      return a.collectibles.vistas.includes(o.target);
    case 'heart':
      return a.collectibles.hearts.includes(o.target);
    default:
      return false;
  }
}

/**
 * Completes current-step objectives that persistent state already satisfies
 * by firing the matching quest events. Returns how many objectives moved.
 */
export function settleQuests(p: Progression): number {
  const c = p.char;
  let moved = 0;
  for (let pass = 0; pass < 6; pass++) {
    let changed = false;
    for (const id of Object.keys(c.quests.active)) {
      const prog = c.quests.active[id];
      const q = prog && allQuests().find((x) => x.id === id);
      const st = q && prog ? currentStep(q, prog) : undefined;
      if (!st || !prog) continue;
      for (let i = 0; i < st.objectives.length; i++) {
        const o = st.objectives[i] as QuestObjective;
        const live = c.quests.active[id];
        if (!live || live.step !== prog.step || (live.counts[i] ?? 0) >= o.count) continue;
        if (!objectiveSatisfied(c, p.account, o)) continue;
        if (o.type === 'flag') {
          if (c.flags[o.target]) p.questEvent({ type: 'flag', target: o.target });
          else p.setFlag(o.target);
        } else if (o.type === 'level') p.questEvent({ type: 'level', level: c.level });
        else p.questEvent({ type: 'interact', target: o.target });
        moved++;
        changed = true;
      }
    }
    if (!changed) break;
  }
  return moved;
}

/** Auto quests (story openers) the character can take right now. */
export function autoQuestsReady(c: CharacterSave): QuestDef[] {
  return allQuests().filter((q) => q.auto && isAvailable(c, q));
}

/**
 * What an NPC says about a quest they gave you that is still in progress
 * (main quests first); null when they have nothing to add.
 */
export function progressLine(c: CharacterSave, npcId: string): string | null {
  const quests = Object.keys(c.quests.active)
    .map((id) => allQuests().find((q) => q.id === id))
    .filter(
      (q): q is QuestDef =>
        !!q && !!q.dialogue.progress && q.giver === npcId && !isReadyToTurnIn(c, q.id),
    );
  quests.sort((a, b) => (a.kind === 'main' ? 0 : 1) - (b.kind === 'main' ? 0 : 1));
  return quests[0]?.dialogue.progress ?? null;
}

// ---------------------------------------------------------------- status & objectives

export type QuestStatus = 'active' | 'ready' | 'available' | 'locked' | 'done';

export function questStatus(c: CharacterSave, q: QuestDef): QuestStatus {
  if (c.quests.done.includes(q.id)) return 'done';
  if (c.quests.active[q.id]) return isReadyToTurnIn(c, q.id) ? 'ready' : 'active';
  return isAvailable(c, q) ? 'available' : 'locked';
}

/** Level at which a quest is offered. */
export const offerLevel = (q: QuestDef): number => q.requiresLevel ?? Math.max(1, q.level - 2);

export interface ObjectiveRow {
  text: string;
  have: number;
  need: number;
  done: boolean;
}

export interface StepView {
  index: number;
  state: 'done' | 'current' | 'future';
  hint?: string;
  objectives: ObjectiveRow[];
}

/** Every step of a quest with progress for the current one. */
export function stepViews(c: CharacterSave, q: QuestDef): StepView[] {
  const prog = c.quests.active[q.id];
  const finished = c.quests.done.includes(q.id);
  return q.steps.map((st, index) => {
    const state: StepView['state'] =
      finished || (prog && index < prog.step)
        ? 'done'
        : prog && index === prog.step
          ? 'current'
          : 'future';
    return {
      index,
      state,
      hint: st.hint,
      objectives: st.objectives.map((o, i) => {
        const have =
          state === 'done'
            ? o.count
            : state === 'current'
              ? Math.min(o.count, prog?.counts[i] ?? 0)
              : 0;
        return { text: o.text, have, need: o.count, done: have >= o.count };
      }),
    };
  });
}

/** Objectives of the current step that still need doing (for markers). */
export function openObjectives(c: CharacterSave, q: QuestDef): QuestObjective[] {
  const prog = c.quests.active[q.id];
  const st = prog ? currentStep(q, prog) : undefined;
  if (!st || !prog) return [];
  return st.objectives.filter((o, i) => (prog.counts[i] ?? 0) < o.count);
}

// ---------------------------------------------------------------- journal lists

export type JournalTab = 'main' | 'side' | 'done';

export interface JournalRow {
  quest: QuestDef;
  status: QuestStatus;
}

export interface JournalGroup {
  key: string;
  title: string;
  sub?: string;
  rows: JournalRow[];
}

const isBounty = (k: QuestKind) => k === 'daily' || k === 'weekly';

/**
 * Journal groups for a tab.
 * - main: story chapters holding the active/ready/next main quest, plus active world quests;
 * - side: active side quests by zone, then side quests available now, then active bounties;
 * - done: completed quests by zone (newest chapter first).
 */
export function journalGroups(
  c: CharacterSave,
  tab: JournalTab,
  opts: {
    zoneName: (id: string) => string;
    chapterOf?: (id: string) => { id: string; name: string; blurb?: string } | null;
  },
): JournalGroup[] {
  const quests = allQuests();
  const out: JournalGroup[] = [];
  const push = (key: string, title: string, rows: JournalRow[], sub?: string) => {
    if (!rows.length) return;
    const g = out.find((x) => x.key === key);
    if (g) g.rows.push(...rows);
    else out.push(sub ? { key, title, sub, rows } : { key, title, rows });
  };
  if (tab === 'main') {
    const shown = new Set<string>();
    for (const q of quests) {
      if (q.kind !== 'main' && q.kind !== 'world') continue;
      const status = questStatus(c, q);
      if (status !== 'active' && status !== 'ready') continue;
      shown.add(q.id);
      const ch = opts.chapterOf?.(q.id);
      push(
        ch?.id ?? q.kind,
        ch?.name ?? (q.kind === 'world' ? 'World' : 'Main story'),
        [{ quest: q, status }],
        ch?.blurb,
      );
    }
    const next = nextMainQuest(c);
    if (next && !shown.has(next.id)) {
      const ch = opts.chapterOf?.(next.id);
      push(
        ch?.id ?? 'main',
        ch?.name ?? 'Main story',
        [{ quest: next, status: questStatus(c, next) }],
        ch?.blurb,
      );
    }
    for (const q of quests) {
      if (q.kind !== 'world' || shown.has(q.id) || questStatus(c, q) !== 'available') continue;
      const ch = opts.chapterOf?.(q.id);
      push(ch?.id ?? 'world', ch?.name ?? 'World', [{ quest: q, status: 'available' }], ch?.blurb);
    }
    return out;
  }
  if (tab === 'side') {
    const active: JournalRow[] = [];
    const avail: JournalRow[] = [];
    const bounties: JournalRow[] = [];
    for (const id of Object.keys(c.quests.active)) {
      const q = quests.find((x) => x.id === id);
      if (!q) continue;
      if (isBounty(q.kind)) bounties.push({ quest: q, status: questStatus(c, q) });
      else if (q.kind === 'side') active.push({ quest: q, status: questStatus(c, q) });
    }
    for (const q of quests)
      if (q.kind === 'side' && questStatus(c, q) === 'available')
        avail.push({ quest: q, status: 'available' });
    const byZone = (rows: JournalRow[], prefix: string, sub?: string) => {
      const zones = [...new Set(rows.map((r) => r.quest.zone))];
      for (const z of zones)
        push(
          `${prefix}:${z}`,
          opts.zoneName(z),
          rows.filter((r) => r.quest.zone === z),
          sub,
        );
    };
    byZone(active, 'active', 'In progress');
    byZone(avail, 'avail', 'Available');
    push('bounties', 'Bounties', bounties, 'From the Bounty Board');
    return out;
  }
  const done = c.quests.done
    .map((id) => quests.find((q) => q.id === id))
    .filter((q): q is QuestDef => !!q && !isBounty(q.kind));
  for (const q of [...done].reverse()) {
    const ch = q.kind === 'side' ? null : opts.chapterOf?.(q.id);
    if (ch) push(ch.id, ch.name, [{ quest: q, status: 'done' }], ch.blurb);
    else
      push(`zone:${q.zone}`, opts.zoneName(q.zone), [{ quest: q, status: 'done' }], 'Side quests');
  }
  return out;
}

/** Counts for the journal tabs. */
export function journalCounts(c: CharacterSave): Record<JournalTab, number> {
  const quests = allQuests();
  let main = 0;
  let side = 0;
  for (const id of Object.keys(c.quests.active)) {
    const q = quests.find((x) => x.id === id);
    if (!q) continue;
    if (q.kind === 'main' || q.kind === 'world') main++;
    else side++;
  }
  const done = c.quests.done.filter((id) => {
    const q = quests.find((x) => x.id === id);
    return !!q && !isBounty(q.kind);
  }).length;
  return { main, side, done };
}
